import type { ReactNode } from 'react'

import type { CashCount, TellerSession, Till, Vault } from '@/api/teller-types'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { countPurposeLabel, tillTypeLabel } from './constants'

/**
 * The read-only panels behind the teller screens.
 *
 * One rule runs through all of them, and it is the rule money screens get wrong: a figure the
 * server sent as null is rendered as a dash, never as zero. A drawer nobody has counted is not a
 * drawer that counted zero, and an over-or-short of null is not a drawer that balanced. `MoneyDisplay`
 * already draws the distinction — these panels only have to stop filling the gap in first.
 */

export function Field({
  label,
  value,
  mono = false,
  hint,
}: {
  label: string
  value: ReactNode
  mono?: boolean
  hint?: string
}) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
      {hint ? <p className="mt-0.5 text-2xs text-muted">{hint}</p> : null}
    </div>
  )
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

/** One line of a cash position: a label, a big figure, and the headroom under a limit. */
export function CashFigure({
  label,
  amountMinor,
  currency,
  hint,
  emphasis = 'strong',
}: {
  label: string
  amountMinor: number | null
  currency: string
  hint?: ReactNode
  emphasis?: 'normal' | 'strong' | 'muted'
}) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <MoneyDisplay
        value={amountMinor}
        currency={currency}
        emphasis={emphasis}
        className="text-left text-base"
      />
      {hint ? <p className="text-2xs text-muted">{hint}</p> : null}
    </div>
  )
}

/**
 * The arithmetic behind a shift, laid out in the order it happens.
 *
 * Opening float, plus what came in, minus what went out, is what the drawer should hold. The
 * declared figure and the over-or-short below it are null for most of the working day, and they are
 * shown as absent for exactly that long.
 */
export function SessionFiguresPanel({ session }: { session: TellerSession }) {
  const declared = session.declaredClosingCashMinor
  const overShort = session.overShortMinor

  return (
    <Section
      title="The drawer"
      description="Opening float, plus cash in, less cash out, is what the drawer should hold."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CashFigure
          label="Opening float"
          amountMinor={session.openingFloatMinor}
          currency={session.currencyCode}
        />
        <CashFigure
          label="Cash in"
          amountMinor={session.cashInMinor}
          currency={session.currencyCode}
          hint="Deposits, security deposits, and collections taken at this window."
        />
        <CashFigure
          label="Cash out"
          amountMinor={session.cashOutMinor}
          currency={session.currencyCode}
          hint="Withdrawals and disbursements paid from this drawer."
        />
        <CashFigure
          label="Expected at close"
          amountMinor={session.expectedClosingCashMinor}
          currency={session.currencyCode}
        />
      </div>

      <div className="mt-4 grid gap-4 border-t border-border-subtle pt-4 sm:grid-cols-2">
        <CashFigure
          label="Declared"
          amountMinor={declared}
          currency={session.currencyCode}
          hint={declared === null ? 'Nobody has declared this drawer yet.' : undefined}
        />
        <div className="min-w-0 space-y-0.5">
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Over or short</p>
          <MoneyDisplay
            value={overShort}
            currency={session.currencyCode}
            emphasis="strong"
            signed
            colourNegative
            className="text-left text-base"
          />
          <p className="text-2xs text-muted">
            {overShort === null
              ? 'Known only once the drawer is declared.'
              : overShort === 0
                ? 'The drawer balanced.'
                : overShort > 0
                  ? 'The drawer holds more than it should. The difference posts to the ledger at close.'
                  : 'The drawer holds less than it should. The difference posts to the ledger at close.'}
          </p>
        </div>
      </div>
    </Section>
  )
}

/** Who worked the shift, on which drawer, and where it stands. */
export function SessionFactsPanel({ session }: { session: TellerSession }) {
  return (
    <Section title="The shift" description="One till, one named person, one business day.">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
        <Field label="Till" value={`${session.tillNumber} · ${tillTypeLabel(session.tillType)}`} />
        <Field label="Teller" value={session.tellerUsername} />
        <Field label="Business date" value={formatBusinessDate(session.businessDate)} />
        <Field label="Opened" value={formatDateTime(session.openedAtUtc)} />
        <Field
          label="Closed"
          value={session.closedAtUtc ? formatDateTime(session.closedAtUtc) : null}
          hint={session.closedAtUtc ? undefined : 'The shift closes after the countersignature, not after the declaration.'}
        />
        <Field
          label="Countersigned by"
          value={session.supervisorUserId}
          mono
          hint={
            session.supervisorUserId
              ? undefined
              : 'Empty where the close has not happened, or where policy required no second person.'
          }
        />
        <Field label="Status" value={<StatusBadge status={session.status} />} />
        <Field label="Currency" value={session.currencyCode} />
      </dl>
    </Section>
  )
}

/**
 * A denomination count as recorded.
 *
 * The subtotals are the server's own multiplication rather than this client's — the sheet on screen
 * has to be the sheet the ledger holds, down to the pesewa.
 */
export function CashCountPanel({ count, expectedMinor }: { count: CashCount; expectedMinor?: number }) {
  const difference = expectedMinor === undefined ? null : count.totalMinor - expectedMinor

  return (
    <Section
      title={countPurposeLabel(count.purpose)}
      description={`Counted by ${count.countedBy} at ${formatDateTime(count.countedAtUtc)}.`}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-md text-sm">
          <thead>
            <tr className="border-b border-border-subtle text-2xs tracking-wide text-muted uppercase">
              <th scope="col" className="py-1.5 text-left font-semibold">
                Denomination
              </th>
              <th scope="col" className="py-1.5 text-right font-semibold">
                Pieces
              </th>
              <th scope="col" className="py-1.5 text-right font-semibold">
                Subtotal
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {count.lines.map((line) => (
              <tr key={line.denominationMinor}>
                <td className="py-1.5">
                  <MoneyDisplay
                    value={line.denominationMinor}
                    currency={count.currencyCode}
                    className="text-left"
                  />
                </td>
                <td className="numeric py-1.5 text-right tabular-nums">{line.pieces}</td>
                <td className="py-1.5">
                  <MoneyDisplay value={line.subtotalMinor} currency={count.currencyCode} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border-strong">
              <td className="py-2 text-xs font-semibold text-heading" colSpan={2}>
                Counted
              </td>
              <td className="py-2">
                <MoneyDisplay
                  value={count.totalMinor}
                  currency={count.currencyCode}
                  emphasis="strong"
                />
              </td>
            </tr>
            {difference === null ? null : (
              <tr>
                <td className="py-1 text-xs text-muted" colSpan={2}>
                  Against expected
                </td>
                <td className="py-1">
                  <MoneyDisplay
                    value={difference}
                    currency={count.currencyCode}
                    signed
                    colourNegative
                  />
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </Section>
  )
}

/** The drawer itself: what it holds, what it may hold, and who is carrying it today. */
export function TillFactsPanel({ till }: { till: Till }) {
  const headroom =
    till.cashHoldingLimitMinor === null ? null : till.cashHoldingLimitMinor - till.cashOnHandMinor

  return (
    <Section title="The drawer" description="Cash on hand against the limit it may carry.">
      <div className="grid gap-4 sm:grid-cols-3">
        <CashFigure label="Cash on hand" amountMinor={till.cashOnHandMinor} currency={till.currencyCode} />
        <CashFigure
          label="Holding limit"
          amountMinor={till.cashHoldingLimitMinor}
          currency={till.currencyCode}
          emphasis="normal"
          hint={till.cashHoldingLimitMinor === null ? 'No limit is set on this drawer.' : undefined}
        />
        <CashFigure
          label="Headroom"
          amountMinor={headroom}
          currency={till.currencyCode}
          emphasis="normal"
          hint={
            headroom === null
              ? 'Without a limit there is nothing to be under.'
              : headroom < 0
                ? 'Over the limit. Bank cash to the vault.'
                : 'What this drawer can still take before it must be banked.'
          }
        />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border-subtle pt-4">
        <Field label="Till number" value={till.tillNumber} mono />
        <Field label="Name" value={till.name} />
        <Field label="Type" value={<Badge tone="neutral">{tillTypeLabel(till.tillType)}</Badge>} />
        <Field label="Status" value={<StatusBadge status={till.status} />} />
        <Field label="Ledger account" value={till.glAccountCode} mono />
        <Field
          label="Assigned to"
          value={till.assignedUserId}
          mono
          hint={
            till.tillType === 'Field'
              ? till.assignedForDate
                ? `For ${formatBusinessDate(till.assignedForDate)} only.`
                : 'Unassigned. A field drawer is live only on the day it is assigned.'
              : 'A counter till is worked through a session, not an assignment.'
          }
        />
      </dl>
    </Section>
  )
}

/**
 * Why a field till has no shift, no count, and no over-or-short.
 *
 * This is not a limitation of the screen. `CashCount` exists only inside the teller session
 * aggregate, and a field till is refused a session outright with
 * `teller.field_till_needs_no_session` — so there is no count sheet to offer, no closing
 * declaration to make, and no over-or-short to post. Saying it here is better than a screen that
 * quietly omits the actions and leaves an officer looking for them.
 */
export function FieldTillNotice() {
  return (
    <Alert tone="info">
      <div className="space-y-1">
        <AlertTitle>A field drawer is not counted here</AlertTitle>
        <AlertDescription>
          A field till is made live by assigning it to an officer for one day, and it is refused a
          teller session — so it has no shift, no denomination count, no closing declaration, and no
          over-or-short posting. Its cash is reconciled by banking it back to the vault, which shows
          on the movements below.
        </AlertDescription>
      </div>
    </Alert>
  )
}

/** Vault cash, for the branch position. */
export function VaultPanel({ vaults }: { vaults: Vault[] }) {
  if (vaults.length === 0) {
    return (
      <Section title="Vaults" description="Where a branch's cash sits when it is not in a drawer.">
        <p className="text-sm text-muted">
          This branch has no vault, or none you can see. A branch vault is created once, under{' '}
          <span className="font-mono text-xs">admin.branch.manage</span>, and every till float comes
          out of it.
        </p>
      </Section>
    )
  }

  return (
    <Section title="Vaults" description="Where a branch's cash sits when it is not in a drawer.">
      <ul className="divide-y divide-border-subtle">
        {vaults.map((vault) => (
          <li key={vault.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-heading">{vault.name}</p>
              <p className="font-mono text-2xs text-muted">{vault.glAccountCode}</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={vault.status} />
              <MoneyDisplay
                value={vault.cashOnHandMinor}
                currency={vault.currencyCode}
                emphasis="strong"
              />
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * What a missing read permission means, said as itself.
 *
 * A 403 on a list is not "there is nothing here", and the difference matters most in this module:
 * `teller.till.view` is held by tellers and branch managers and by no field role at all, so the
 * officer actually carrying a field drawer cannot see its balance. An empty table would tell them
 * the drawer does not exist.
 */
export function PermissionNotice({ permission, what }: { permission: string; what: string }) {
  return (
    <Alert tone="warning">
      <div className="space-y-1">
        <AlertTitle>{what} is not visible to your role</AlertTitle>
        <AlertDescription>
          Reading it needs <span className="font-mono text-xs">{permission}</span>. This is a
          restriction, not an empty branch — ask an administrator rather than assuming there is
          nothing to see.
        </AlertDescription>
      </div>
    </Alert>
  )
}
